import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_STATUSES, ORDER_STATUSES, LINE_TYPES } from '../firebase/schema';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';
import { 
  Box, 
  Typography,
  ThemeProvider,
  createTheme,
  Paper,
  Grid,
  CircularProgress
} from '@mui/material';
import styled from '@emotion/styled';
import { 
  startOfDay, 
  startOfMonth, 
  endOfMonth,
  format,
  differenceInCalendarDays,
  isWeekend
} from 'date-fns';
import { calculateOptimalPrintlist } from '../utils/printListCalculations';

/**
 * Safely parse a date value from various formats
 * Handles: Firestore Timestamp, Date object, ISO string, or null/undefined
 */
const parseDate = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // Firestore Timestamp (has toDate method)
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // Firestore Timestamp might also have seconds/nanoseconds
    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // JavaScript Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // String or number - let Date parse it
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    
    return null;
  } catch (error) {
    console.warn('Error parsing date:', dateValue, error);
    return null;
  }
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1C1C1C',
      paper: '#2C2C2C'
    },
    primary: {
      main: '#98FB98'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    button: {
      textTransform: 'uppercase'
    }
  }
});

const PageContainer = styled(Box)`
  width: 100%;
  padding: 80px 24px 24px 24px;
  background: var(--al-bg);
  min-height: 100vh;
`;

const StatCard = styled(Paper)`
  padding: 24px;
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(var(--al-primary-rgb), 0.2);
  }
`;

const StatValue = styled(Typography)`
  color: var(--al-primary);
  font-size: 2.5rem;
  font-weight: 700;
  margin: 12px 0;
  text-transform: uppercase;
`;

const StatLabel = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const StatSubtext = styled(Typography)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  margin-top: 8px;
`;

const ChartCard = styled(Paper)`
  padding: 24px;
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  height: 100%;
`;

const ChartTitle = styled(Typography)`
  color: var(--al-primary);
  font-size: 1.25rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
`;

const RevenueGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
`;

const RevenueItem = styled(Box)`
  padding: 16px;
  background: rgba(var(--al-primary-rgb), 0.1);
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  border-radius: 4px;
`;

const RevenueLabel = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const RevenueValue = styled(Typography)`
  color: var(--al-primary);
  font-size: 1.5rem;
  font-weight: 700;
`;

const ProductionDashboard = () => {
  const { facilityId } = useFacility();
  const [loading, setLoading] = useState(true);
  
  // This Month's Financial Stats
  const [thisMonthStats, setThisMonthStats] = useState({
    invoiced: 0,           // Completed/archived orders this month
    inProduction: 0,       // Active orders due this month
    totalExpected: 0,      // Sum of invoiced + in production
    avgCogsPercent: 0,     // Average COGS % across all jobs
    totalCogs: 0,          // Total COGS dollars
    jobsCompleted: 0,      // Number of completed jobs
    jobsRemaining: 0       // Number of jobs still to complete
  });
  
  // 10-Day Production Forecast
  const [tenDayForecast, setTenDayForecast] = useState([]);
  const [tenDayTotals, setTenDayTotals] = useState({
    totalHours: 0,
    totalPieces: 0,
    totalJobs: 0,
    totalValue: 0
  });
  
  // 10-Day Stats by Line Type
  const [tenDayByType, setTenDayByType] = useState({
    screenPrint: { hours: 0, lines: 0, pieces: 0 },
    dtg: { hours: 0, lines: 0, pieces: 0 },
    dtf: { hours: 0, lines: 0, pieces: 0 },
    embroidery: { hours: 0, lines: 0, pieces: 0 }
  });

  const WORKDAY_HOURS = 7; // 7 hours available per day
  const WORKDAY_MINUTES = WORKDAY_HOURS * 60;

  useEffect(() => {
    fetchDashboardData();
  }, [facilityId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const today = startOfDay(new Date());
      const startOfThisMonth = startOfMonth(today);
      const endOfThisMonth = endOfMonth(today);

      // Fetch all order lines
      const linesQuery = query(collection(db, 'orderLines'));
      const linesSnapshot = await getDocs(linesQuery);
      
      // Fetch all orders for customer names, due dates, and order-level data
      const ordersQuery = query(collection(db, 'orders'));
      const ordersSnapshot = await getDocs(ordersQuery);
      const orderMap = new Map(
        ordersSnapshot.docs.map(doc => [doc.id, { id: doc.id, ...doc.data() }])
      );

      const allLines = filterByFacility(
        linesSnapshot.docs.map(doc => {
          const lineData = doc.data();
          const orderData = orderMap.get(lineData.orderId) || {};
          
          return {
            id: doc.id,
            ...lineData,
            facilityId: lineData.facilityId || orderData.facilityId,
            dueDate: orderData.dueDate || lineData.dueDate,
            customerName: orderData.customerName || 'N/A',
            orderNumber: orderData.poNumber || 'N/A',
            orderStatus: orderData.status
          };
        }),
        facilityId
      );

      // ========== THIS MONTH STATS ==========
      // Lines with due dates this month
      const thisMonthLines = allLines.filter(line => {
        if (!line.dueDate) return false;
        const due = parseDate(line.dueDate);
        if (!due) return false;
        const dueDay = startOfDay(due);
        return dueDay >= startOfThisMonth && dueDay <= endOfThisMonth;
      });
      
      // Debug logging for this month's stats
      console.log('📊 Production Dashboard Stats:');
      console.log(`   Total lines fetched: ${allLines.length}`);
      console.log(`   Lines due this month: ${thisMonthLines.length}`);

      // Completed/Invoiced this month (status is COMPLETED or order is archived)
      const completedThisMonth = thisMonthLines.filter(line => 
        line.status === LINE_STATUSES.COMPLETED || 
        line.printed ||
        line.orderStatus === ORDER_STATUSES.COMPLETED
      );
      
      const invoicedRevenue = completedThisMonth.reduce((sum, line) => {
        const quantity = line.totalQuantity || 0;
        const price = line.pricePerPiece || 0;
        return sum + (quantity * price);
      }, 0);

      const invoicedCogs = completedThisMonth.reduce((sum, line) => {
        const quantity = line.totalQuantity || 0;
        const cogs = line.cogs || 0;
        return sum + (quantity * cogs);
      }, 0);

      // In Production this month (not yet completed)
      const inProductionThisMonth = thisMonthLines.filter(line => 
        line.status !== LINE_STATUSES.COMPLETED && 
        !line.printed &&
        line.orderStatus !== ORDER_STATUSES.COMPLETED
      );
      
      const inProductionRevenue = inProductionThisMonth.reduce((sum, line) => {
        const quantity = line.totalQuantity || 0;
        const price = line.pricePerPiece || 0;
        return sum + (quantity * price);
      }, 0);

      const inProductionCogs = inProductionThisMonth.reduce((sum, line) => {
        const quantity = line.totalQuantity || 0;
        const cogs = line.cogs || 0;
        return sum + (quantity * cogs);
      }, 0);

      // Calculate average COGS %
      const totalRevenue = invoicedRevenue + inProductionRevenue;
      const totalCogs = invoicedCogs + inProductionCogs;
      const avgCogsPercent = totalRevenue > 0 ? (totalCogs / totalRevenue) * 100 : 0;

      // More debug logging
      console.log(`   Completed this month: ${completedThisMonth.length} lines = $${invoicedRevenue.toFixed(2)}`);
      console.log(`   In production this month: ${inProductionThisMonth.length} lines = $${inProductionRevenue.toFixed(2)}`);
      console.log(`   Expected total: $${totalRevenue.toFixed(2)}`);
      
      // Log sample lines to check data structure
      if (thisMonthLines.length > 0) {
        console.log('   Sample lines from this month:');
        thisMonthLines.slice(0, 3).forEach((line, i) => {
          const lineValue = (line.totalQuantity || 0) * (line.pricePerPiece || 0);
          console.log(`   [${i}] ${line.designName || 'No name'}: qty=${line.totalQuantity}, price=$${line.pricePerPiece}, value=$${lineValue.toFixed(2)}, status=${line.status}, dueDate=${line.dueDate}`);
        });
      }
      
      // Log lines with zero value to diagnose
      const zeroValueLines = thisMonthLines.filter(line => {
        const value = (line.totalQuantity || 0) * (line.pricePerPiece || 0);
        return value === 0;
      });
      if (zeroValueLines.length > 0) {
        console.log(`   ⚠️ ${zeroValueLines.length} lines have $0 value (missing qty or price)`);
        zeroValueLines.slice(0, 3).forEach((line, i) => {
          console.log(`      [${i}] ${line.designName}: qty=${line.totalQuantity}, price=${line.pricePerPiece}`);
        });
      }

      setThisMonthStats({
        invoiced: invoicedRevenue,
        inProduction: inProductionRevenue,
        totalExpected: totalRevenue,
        avgCogsPercent,
        totalCogs,
        jobsCompleted: completedThisMonth.length,
        jobsRemaining: inProductionThisMonth.length
      });

      // ========== 10 BUSINESS DAY PRODUCTION FORECAST ==========
      const activeLines = allLines.filter(line => 
        line.status !== LINE_STATUSES.COMPLETED && 
        !line.printed &&
        line.orderStatus !== ORDER_STATUSES.COMPLETED &&
        line.dueDate
      );

      // Helper: Go back N business days from a date
      const subtractBusinessDays = (date, numDays) => {
        let result = new Date(date);
        let daysSubtracted = 0;
        
        while (daysSubtracted < numDays) {
          result = new Date(result.getTime() - 24 * 60 * 60 * 1000);
          // Only count weekdays
          if (!isWeekend(result)) {
            daysSubtracted++;
          }
        }
        return result;
      };

      // Helper: Get the EXPECTED production date for a due date
      // Goal: Produce 1-2 business days BEFORE due date to level out peaks
      // Rules:
      // 1. Weekend due dates first get adjusted to Friday
      // 2. Then back up 1 business day for the expected production date
      // 3. Never earlier than today
      const getExpectedProductionDate = (dueDate, orderCreatedAt = null) => {
        const parsedDue = parseDate(dueDate);
        if (!parsedDue) return today; // Fallback to today if can't parse
        
        let due = startOfDay(parsedDue);
        const dayOfWeek = due.getDay(); // 0 = Sunday, 6 = Saturday
        
        // Step 1: Adjust weekend due dates to Friday first
        if (dayOfWeek === 0) {
          // Sunday -> Friday (2 days before)
          due = new Date(due.getTime() - 2 * 24 * 60 * 60 * 1000);
        } else if (dayOfWeek === 6) {
          // Saturday -> Friday (1 day before)
          due = new Date(due.getTime() - 1 * 24 * 60 * 60 * 1000);
        }
        
        // Step 2: Back up 1 business day for expected production
        // This creates buffer and helps level peaks
        let expectedDate = subtractBusinessDays(due, 1);
        
        // Step 3: Can't produce before today
        if (expectedDate < today) {
          expectedDate = today;
        }
        
        // Step 4: Can't produce before order was created (if we have that data)
        if (orderCreatedAt) {
          const parsedCreated = parseDate(orderCreatedAt);
          if (parsedCreated) {
            const created = startOfDay(parsedCreated);
            if (expectedDate < created) {
              expectedDate = created;
            }
          }
        }
        
        return expectedDate;
      };

      // Build array of next 10 BUSINESS days (skip weekends)
      const businessDays = [];
      let currentDate = new Date(today);
      
      while (businessDays.length < 10) {
        if (!isWeekend(currentDate)) {
          const isToday = differenceInCalendarDays(currentDate, today) === 0;
          const isTomorrow = differenceInCalendarDays(currentDate, today) === 1;
          
          businessDays.push({
            date: startOfDay(new Date(currentDate)),
            dayName: isToday ? 'TODAY' : isTomorrow ? 'TMROW' : format(currentDate, 'EEE M/d').toUpperCase(),
            fullDate: format(currentDate, 'MMM d'),
            lines: [],
            totalMinutes: 0,
            hours: 0,
            pieces: 0,
            value: 0
          });
        }
        currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
      }

      // Assign lines to business days based on EXPECTED production date
      // (1 business day before due date to help level out peaks)
      activeLines.forEach(line => {
        if (!line.dueDate) return;
        
        // Use expected production date (1 biz day before due, adjusted for weekends)
        const expectedProdDate = getExpectedProductionDate(
          line.dueDate, 
          line.createdAt || line.orderCreatedAt
        );
        const daysFromToday = differenceInCalendarDays(expectedProdDate, today);
        
        // Find the matching business day or put overdue in today
        if (daysFromToday < 0) {
          // Overdue - goes into today (first business day)
          businessDays[0].lines.push(line);
        } else {
          // Find the business day that matches the expected production date
          const matchingDayIndex = businessDays.findIndex(day => 
            differenceInCalendarDays(day.date, expectedProdDate) === 0
          );
          
          if (matchingDayIndex !== -1) {
            businessDays[matchingDayIndex].lines.push(line);
          }
          // Jobs beyond 10 business days are not shown
        }
      });

      // Helper to get hours from lines with estimatedTime
      const getHoursFromLinesWithTime = (lines) => {
        return lines.reduce((sum, line) => sum + (line.estimatedTime || 0), 0) / 60;
      };
      
      const getPiecesFromLines = (lines) => {
        return lines.reduce((sum, line) => sum + (line.totalQuantity || 0), 0);
      };

      // Calculate production time and values for each day WITH breakdown by type
      let totalForecastHours = 0;
      let totalForecastPieces = 0;
      let totalForecastJobs = 0;
      let totalForecastValue = 0;

      const forecastWithStats = businessDays.map(day => {
        const pieces = day.lines.reduce((sum, line) => sum + (line.totalQuantity || 0), 0);
        const value = day.lines.reduce((sum, line) => {
          const qty = line.totalQuantity || 0;
          const price = line.pricePerPiece || 0;
          return sum + (qty * price);
        }, 0);

        let hours = 0;
        let byType = {
          screenPrint: { hours: 0, lines: 0, pieces: 0 },
          dtg: { hours: 0, lines: 0, pieces: 0 },
          dtf: { hours: 0, lines: 0, pieces: 0 },
          embroidery: { hours: 0, lines: 0, pieces: 0 }
        };
        
        if (day.lines.length > 0) {
          const result = calculateOptimalPrintlist(day.lines);
          const totalMinutes = result.totalEstimatedTime || 0;
          hours = totalMinutes / 60;
          
          // Calculate breakdown by type
          byType = {
            screenPrint: {
              hours: getHoursFromLinesWithTime(result.screenPrintJobs),
              lines: result.screenPrintJobs.length,
              pieces: getPiecesFromLines(result.screenPrintJobs)
            },
            dtg: {
              hours: getHoursFromLinesWithTime(result.dtgJobs),
              lines: result.dtgJobs.length,
              pieces: getPiecesFromLines(result.dtgJobs)
            },
            dtf: {
              hours: getHoursFromLinesWithTime(result.dtfJobs),
              lines: result.dtfJobs.length,
              pieces: getPiecesFromLines(result.dtfJobs)
            },
            embroidery: {
              hours: getHoursFromLinesWithTime(result.embroideryJobs),
              lines: result.embroideryJobs.length,
              pieces: getPiecesFromLines(result.embroideryJobs)
            }
          };
        }

        totalForecastHours += hours;
        totalForecastPieces += pieces;
        totalForecastJobs += day.lines.length;
        totalForecastValue += value;

        return {
          ...day,
          hours,
          pieces,
          value,
          byType,
          jobCount: day.lines.length,
          capacity: Math.min((hours / WORKDAY_HOURS) * 100, 150) // Allow over 100% to show overloaded days
        };
      });

      setTenDayForecast(forecastWithStats);
      setTenDayTotals({
        totalHours: totalForecastHours,
        totalPieces: totalForecastPieces,
        totalJobs: totalForecastJobs,
        totalValue: totalForecastValue
      });

      // ========== CALCULATE STATS BY LINE TYPE ==========
      // Get all lines from the 10-day forecast and run through the printlist calculator
      const allForecastLines = businessDays.flatMap(day => day.lines);
      
      // Use calculateOptimalPrintlist to get properly categorized results
      // This ensures we use the same logic for categorization as the main calculation
      const printlistResult = calculateOptimalPrintlist(allForecastLines);
      
      const getPiecesForLines = (lines) => {
        return lines.reduce((sum, line) => sum + (line.totalQuantity || 0), 0);
      };
      
      const getHoursFromLines = (lines) => {
        return lines.reduce((sum, line) => sum + (line.estimatedTime || 0), 0) / 60;
      };

      // The printlist result already has jobs categorized with their estimated times
      setTenDayByType({
        screenPrint: {
          hours: getHoursFromLines(printlistResult.screenPrintJobs),
          lines: printlistResult.screenPrintJobs.length,
          pieces: getPiecesForLines(printlistResult.screenPrintJobs)
        },
        dtg: {
          hours: getHoursFromLines(printlistResult.dtgJobs),
          lines: printlistResult.dtgJobs.length,
          pieces: getPiecesForLines(printlistResult.dtgJobs)
        },
        dtf: {
          hours: getHoursFromLines(printlistResult.dtfJobs),
          lines: printlistResult.dtfJobs.length,
          pieces: getPiecesForLines(printlistResult.dtfJobs)
        },
        embroidery: {
          hours: getHoursFromLines(printlistResult.embroideryJobs),
          lines: printlistResult.embroideryJobs.length,
          pieces: getPiecesForLines(printlistResult.embroideryJobs)
        }
      });
      
      // Log any uncategorized lines (lines that got time calculated but aren't in any category)
      const categorizedCount = printlistResult.screenPrintJobs.length + 
                               printlistResult.dtgJobs.length + 
                               printlistResult.dtfJobs.length + 
                               printlistResult.embroideryJobs.length;
      if (categorizedCount !== allForecastLines.length) {
        console.log(`⚠️ ${allForecastLines.length - categorizedCount} lines not categorized. Check type field values.`);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <PageContainer>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress sx={{ color: 'var(--al-primary)' }} />
          </Box>
        </PageContainer>
      </ThemeProvider>
    );
  }

  const currentMonthName = format(new Date(), 'MMMM');

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <Typography 
          variant="h4" 
          sx={{ 
            color: 'var(--al-primary)', 
            fontWeight: 700, 
            mb: 4, 
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
        >
          Production Dashboard
        </Typography>

        <Grid container spacing={3}>
          {/* ========== THIS MONTH'S FINANCIALS ========== */}
          <Grid item xs={12}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', textTransform: 'uppercase', mb: 1 }}>
              {currentMonthName} Financials
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>{currentMonthName} Invoiced</StatLabel>
              <StatValue sx={{ color: '#4CAF50' }}>
                ${thisMonthStats.invoiced.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </StatValue>
              <StatSubtext>
                {thisMonthStats.jobsCompleted} jobs completed
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>Still In Production</StatLabel>
              <StatValue sx={{ color: '#FFC107' }}>
                ${thisMonthStats.inProduction.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </StatValue>
              <StatSubtext>
                {thisMonthStats.jobsRemaining} jobs remaining
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>Expected {currentMonthName} Total</StatLabel>
              <StatValue>
                ${thisMonthStats.totalExpected.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </StatValue>
              <StatSubtext>
                ${(thisMonthStats.totalExpected - thisMonthStats.totalCogs).toLocaleString(undefined, { maximumFractionDigits: 0 })} post-COGS
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>Avg COGS %</StatLabel>
              <StatValue sx={{ 
                color: thisMonthStats.avgCogsPercent > 40 ? '#FF4C4C' : 
                       thisMonthStats.avgCogsPercent > 30 ? '#FFC107' : 'var(--al-primary)' 
              }}>
                {thisMonthStats.avgCogsPercent.toFixed(1)}%
              </StatValue>
              <StatSubtext>
                ${thisMonthStats.totalCogs.toLocaleString(undefined, { maximumFractionDigits: 0 })} in costs
              </StatSubtext>
            </StatCard>
          </Grid>

          {/* ========== 10 BUSINESS DAY TOTALS BY TYPE ========== */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>Screen Print</StatLabel>
              <StatValue>
                {tenDayByType.screenPrint.hours.toFixed(1)}H
              </StatValue>
              <StatSubtext>
                {tenDayByType.screenPrint.lines} lines • {tenDayByType.screenPrint.pieces.toLocaleString()} pcs
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>DTG</StatLabel>
              <StatValue sx={{ color: '#64B5F6' }}>
                {tenDayByType.dtg.hours.toFixed(1)}H
              </StatValue>
              <StatSubtext>
                {tenDayByType.dtg.lines} lines • {tenDayByType.dtg.pieces.toLocaleString()} pcs
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>DTF</StatLabel>
              <StatValue sx={{ color: '#FFB74D' }}>
                {tenDayByType.dtf.hours.toFixed(1)}H
              </StatValue>
              <StatSubtext>
                {tenDayByType.dtf.lines} lines • {tenDayByType.dtf.pieces.toLocaleString()} pcs
              </StatSubtext>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatLabel>Embroidery</StatLabel>
              <StatValue sx={{ color: '#CE93D8' }}>
                {tenDayByType.embroidery.hours.toFixed(1)}H
              </StatValue>
              <StatSubtext>
                {tenDayByType.embroidery.lines} lines • {tenDayByType.embroidery.pieces.toLocaleString()} pcs
              </StatSubtext>
            </StatCard>
          </Grid>

          {/* ========== 10 BUSINESS DAY HEAT MAP ========== */}
          <Grid item xs={12}>
            <ChartCard>
              <ChartTitle>10 Business Day Production Forecast</ChartTitle>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem', mt: -2, mb: 2 }}>
                Hours by machine type • Green ≤6h • Orange 6-8h • Red &gt;8h
              </Typography>
              
              {/* Heat Map Table */}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '0.85rem'
                }}>
                  <thead>
                    <tr>
                      <th style={{ 
                        textAlign: 'left', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>DAY</th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>SCREEN</th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>DTG</th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>DTF</th>
                      <th style={{ 
                        textAlign: 'center', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>EMB</th>
                      <th style={{ 
                        textAlign: 'right', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>JOBS</th>
                      <th style={{ 
                        textAlign: 'right', 
                        padding: '8px 12px', 
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>VALUE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenDayForecast.map((day, index) => {
                      // Text color based on hours
                      const getTextColor = (hours) => {
                        if (hours === 0) return 'rgba(255,255,255,0.2)';
                        if (hours <= 6) return 'var(--al-primary)';  // Green
                        if (hours <= 8) return '#FFB74D';  // Orange
                        return '#FF6B6B';                   // Red
                      };

                      const sp = day.byType?.screenPrint?.hours || 0;
                      const dtg = day.byType?.dtg?.hours || 0;
                      const dtf = day.byType?.dtf?.hours || 0;
                      const emb = day.byType?.embroidery?.hours || 0;

                      return (
                        <tr key={index}>
                          <td style={{ 
                            padding: '10px 12px', 
                            color: '#fff',
                            fontWeight: 500,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {day.dayName}
                          </td>
                          <td style={{ 
                            padding: '8px 12px', 
                            textAlign: 'center',
                            color: getTextColor(sp),
                            fontWeight: sp > 0 ? 600 : 400,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {sp > 0 ? sp.toFixed(1) : '—'}
                          </td>
                          <td style={{ 
                            padding: '8px 12px', 
                            textAlign: 'center',
                            color: getTextColor(dtg),
                            fontWeight: dtg > 0 ? 600 : 400,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {dtg > 0 ? dtg.toFixed(1) : '—'}
                          </td>
                          <td style={{ 
                            padding: '8px 12px', 
                            textAlign: 'center',
                            color: getTextColor(dtf),
                            fontWeight: dtf > 0 ? 600 : 400,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {dtf > 0 ? dtf.toFixed(1) : '—'}
                          </td>
                          <td style={{ 
                            padding: '8px 12px', 
                            textAlign: 'center',
                            color: getTextColor(emb),
                            fontWeight: emb > 0 ? 600 : 400,
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {emb > 0 ? emb.toFixed(1) : '—'}
                          </td>
                          <td style={{ 
                            padding: '10px 12px', 
                            textAlign: 'right',
                            color: 'rgba(255,255,255,0.7)',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {day.jobCount}
                          </td>
                          <td style={{ 
                            padding: '10px 12px', 
                            textAlign: 'right',
                            color: '#fff',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            ${day.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </ChartCard>
          </Grid>
        </Grid>
      </PageContainer>
    </ThemeProvider>
  );
};

export default ProductionDashboard;

